// SKR (Safe Keeping Receipt) data store.
// Records are persisted in the browser (localStorage) so admin entries survive reloads.
// This is a prototype store — swap for a real backend/database when ready.
export type StoredItem = {
  id: string
  category: string // e.g. Precious Metals, Documents, Jewelry, Art
  description: string
  quantity: string // free text so "3 bars" / "1 set" etc. all work
  estimatedValue: string // display string, e.g. "$120,000"
}
export type SKRRecord = {
  code: string // 8-digit code
  holderName: string
  accountRef: string
  facility: string
  issueDate: string // ISO date
  expiryDate: string // ISO date
  status: 'Active' | 'Released' | 'On Hold'
  items: StoredItem[]
  notes?: string
}
const STORAGE_KEY = 'steca_skr_records_v1'
const SEED: SKRRecord[] = [
  {
    code: '48120736',
    holderName: 'Jonathan A. Meyer',
    accountRef: 'STG-2026-0142',
    facility: 'Dar es Salaam Vault — Chamber 3',
    issueDate: '2026-01-14',
    expiryDate: '2027-01-14',
    status: 'Active',
    items: [
      {
        id: 'i1',
        category: 'Precious Metals',
        description: 'Gold bullion bars, 1kg, 999.9 fine (LBMA)',
        quantity: '5 bars',
        estimatedValue: '$325,000',
      },
      {
        id: 'i2',
        category: 'Precious Metals',
        description: 'Silver coins, 1oz American Eagle',
        quantity: '200 coins',
        estimatedValue: '$6,400',
      },
      {
        id: 'i3',
        category: 'Documents',
        description: 'Sealed legal deed envelope',
        quantity: '1 envelope',
        estimatedValue: '—',
      },
    ],
    notes: 'Deposited under dual-custody protocol. Insurance policy STG-INS-0142.',
  },
  {
    code: '90057214',
    holderName: 'Amara Okafor',
    accountRef: 'STG-2026-0189',
    facility: 'Dar es Salaam Vault — Chamber 1',
    issueDate: '2026-03-02',
    expiryDate: '2027-03-02',
    status: 'Active',
    items: [
      {
        id: 'i1',
        category: 'Jewelry',
        description: 'Diamond necklace set, GIA certified',
        quantity: '1 set',
        estimatedValue: '$210,000',
      },
      {
        id: 'i2',
        category: 'Fine Art',
        description: 'Framed oil painting (private collection)',
        quantity: '1 piece',
        estimatedValue: '$85,000',
      },
    ],
    notes: 'Climate-controlled storage. Handling requires two authorized signatures.',
  },
  {
    code: '91199202',
    holderName: 'GEN DAVID MUTOMBO MAWAMBA & Mr. JEAN LUC SAFARI',
    accountRef: 'STG-2026-0142',
    facility: 'Dar es Salaam Vault — Chamber 3',
    issueDate: '2026-07-17',
    expiryDate: '2027-08-17',
    status: 'Active',
    items: [
      {
        id: 'i1',
        category: 'N/A',
        description: 'FOUR BALLETS CONTAIN 200.14KG',
        quantity: 'N/A',
        estimatedValue: 'N/A',
      },
    ],
    notes: 'Cargo details are confidential.',
  },
]
function read(): SKRRecord[] {
  if (typeof window === 'undefined') return SEED
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(SEED))
      return SEED
    }
    const records = JSON.parse(raw) as SKRRecord[]
    const confidentialRecord = SEED.find((record) => record.code === '91199202')
    if (confidentialRecord) {
      const existingIndex = records.findIndex((record) => record.code === confidentialRecord.code)
      const updatedRecords = existingIndex >= 0
        ? records.map((record) => (record.code === confidentialRecord.code ? confidentialRecord : record))
        : [...records, confidentialRecord]
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedRecords))
      return updatedRecords
    }
    return records
  } catch {
    return SEED
  }
}
function write(records: SKRRecord[]): void {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(records))
}
export function getAllRecords(): SKRRecord[] {
  return read()
}
export function findByCode(code: string): SKRRecord | undefined {
  const clean = code.trim()
  return read().find((r) => r.code === clean)
}
export function saveRecord(record: SKRRecord): { ok: boolean; error?: string } {
  const records = read()
  const idx = records.findIndex((r) => r.code === record.code)
  if (idx >= 0) {
    records[idx] = record
  } else {
    records.push(record)
  }
  write(records)
  return { ok: true }
}
export function deleteRecord(code: string): void {
  write(read().filter((r) => r.code !== code))
}
export const ITEM_CATEGORIES = [
  'Precious Metals',
  'Jewelry',
  'Fine Art',
  'Documents',
  'Watches',
  'Collectibles',
  'Other',
]
